<script lang="ts">
	import { tweened } from 'svelte/motion';
	import { cubicOut } from 'svelte/easing';
	import { format } from 'svelte-i18n';
	import { Popover, Icon } from '@Shelly/shelly-design-system';
	import { user_data } from 'src/stores/account/user';
import { ai_prompt_count } from 'src/stores/features/ai-model';
	import { MAX_AI_MODEL_LIMIT, AI_PROMPT_LIMIT_PREMIUM } from '../../constants';

	$: isPremium = $user_data.premium > 0;
	$: promptLimit = isPremium ? AI_PROMPT_LIMIT_PREMIUM : MAX_AI_MODEL_LIMIT;
	$: promptUsagePercent = Math.min(Math.round(($ai_prompt_count / promptLimit) * 100), 100);

	const animatedPercent = tweened(0, { duration: 600, easing: cubicOut });
	$: animatedPercent.set(promptUsagePercent);
	$: displayPercent = Math.round($animatedPercent);
	$: displayDigits = String(displayPercent).split('');
</script>

<Popover
	placement="top"
	type="hover"
	title={$format('ai_prompt_usage_title', { default: 'Prompt Usage' })}
	hideCloseBtn
>
	<svelte:fragment slot="default">
		<span
			class="prompt-usage"
			class:prompt-usage--premium={isPremium}
			class:prompt-usage--warning={!isPremium && promptUsagePercent >= 80}
			class:prompt-usage--danger={!isPremium && promptUsagePercent >= 100}
		>
			{#if isPremium}
				<span class="gem-icon">
					<Icon size="s" icon="fas fa-gem" class="gem-icon-color" />
				</span>
			{/if}
			<span class="rolling-number" aria-label="{displayPercent}%">
				{#each displayDigits as digit, i (i)}
					<span class="digit-slot">
						<span class="digit-reel" style="transform: translateY(-{Number(digit) * 10}%)">
							{#each Array(10) as _, n}
								<span class="digit-face">{n}</span>
							{/each}
						</span>
					</span>
				{/each}
			</span>%
		</span>
	</svelte:fragment>
	<svelte:fragment slot="customContent">
		<div class="usage-info">
			<p>
				{$ai_prompt_count} / {promptLimit}
				{$format('ai_prompts_used', { default: 'prompts used' })}
			</p>
			<p>
				{#if isPremium}
					{$format('ai_prompt_premium_desc', {
						default: 'Premium users get 50 prompts per day.',
					})}
				{:else}
					{$format('ai_prompt_free_desc', { default: 'Free users get 10 prompts per day.' })}
				{/if}
			</p>
			<p>{$format('ai_prompt_reset_desc', { default: 'Prompts reset daily at midnight.' })}</p>
		</div>
	</svelte:fragment>
</Popover>

<style>
	.prompt-usage {
		display: inline-flex;
		align-items: center;
		font-family: 'Inter', sans-serif;
		font-size: 12px;
		font-weight: 500;
		line-height: 16px;
		padding: 4px 8px;
		border-radius: 6px;
		background: var(--color-background-1, #151617);
		border: 1px solid var(--color-border-2, #5b5f66);
		color: var(--color-text-secondary, white);
		white-space: nowrap;
		transition:
			color 0.3s,
			border-color 0.3s,
			background 0.3s;
	}

	.prompt-usage--premium {
		background: linear-gradient(135deg, #f5d88a 0%, #c9a227 50%, #d4af37 100%);
		border-color: #c9a227;
		color: #2d2d2d;
	}

	.gem-icon {
		display: flex;
		align-items: center;
		flex-shrink: 0;
	}

	.gem-icon :global(.gem-icon-color) {
		color: #2d2d2d;
	}

	.prompt-usage--warning {
		color: var(--color-warning, #ff9800);
		border-color: var(--color-warning, #ff9800);
	}

	.prompt-usage--danger {
		color: var(--color-danger, #f44336);
		border-color: var(--color-danger, #f44336);
	}

	.rolling-number {
		display: inline-flex;
		overflow: hidden;
	}

	.digit-slot {
		display: inline-block;
		height: 1em;
		overflow: hidden;
		line-height: 1;
	}

	.digit-reel {
		display: flex;
		flex-direction: column;
		transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
	}

	.digit-face {
		height: 1em;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.usage-info {
		padding: 4px 0;
		font-size: 13px;
		line-height: 1.5;
		color: var(--color-text-primary);
	}

	.usage-info p {
		margin: 4px 0;
	}

	.usage-info p:first-child {
		font-weight: 600;
	}
</style>
